import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ListChecks } from 'lucide-react';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

/** Refresh lives in AdminHeader (top bar) so every admin page can reload data. */
export function AdminPageHeader({ title, subtitle, actions = null, className = '' }) {
 return (
 <div
 className={`mb-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ${className}`}
 >
 <div className="min-w-0">
 <h1 className="mb-0.5 text-lg font-semibold tracking-tight text-ink">
 {title}
 </h1>
 {subtitle ? (
 <p className="max-w-2xl text-sm leading-relaxed text-ink-secondary">
 {subtitle}
 </p>
 ) : null}
 </div>
 {actions ? (
 <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
 {actions}
 </div>
 ) : null}
 </div>
 );
}

export function AdminGuideCallout({
 title,
 children,
 steps = [],
 tone = 'slate',
 icon: Icon = ListChecks,
 action = null,
 className = '',
}) {
 const tones = {
 slate: {
 wrap: 'border-gray-200 bg-surface',
 iconWrap:
 'bg-surface-muted border-gray-200 text-gray-700',
 title: 'text-ink',
 body: 'text-ink-secondary',
 step: 'text-ink-tertiary',
 },
 sky: {
 wrap: 'border-border/80 bg-surface-muted/70',
 iconWrap:
 'bg-surface border-border text-ink',
 title: 'text-ink',
 body: 'text-ink/80',
 step: 'text-ink',
 },
 amber: {
 wrap: 'border-amber-200/80 bg-amber-50/80',
 iconWrap:
 'bg-surface border-amber-100 text-amber-700',
 title: 'text-amber-950',
 body: 'text-amber-900/80',
 step: 'text-amber-700',
 },
 };
 const t = tones[tone] || tones.slate;

 return (
 <div
 className={`rounded-lg border px-4 py-3.5 sm:px-5 sm:py-4 shadow-sm ${t.wrap} ${className}`}
 >
 <div className="flex items-start gap-3">
 <div
 className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${t.iconWrap}`}
 >
 <Icon className="w-3.5 h-3.5" aria-hidden />
 </div>
 <div className="min-w-0 flex-1">
 {title ? (
 <h2 className={`text-sm font-semibold tracking-tight ${t.title}`}>{title}</h2>
 ) : null}
 {children ? <div className={`mt-1 text-sm leading-relaxed ${t.body}`}>{children}</div> : null}
 {steps.length > 0 ? (
 <ol className={`mt-2.5 space-y-1 text-sm leading-snug ${t.title}`}>
 {steps.map((step, i) => (
 <li key={i} className="flex gap-2">
 <span className={`font-semibold shrink-0 tabular-nums ${t.step}`}>{i + 1}.</span>
 <span className={t.body}>{step}</span>
 </li>
 ))}
 </ol>
 ) : null}
 {action ? <div className="mt-3">{action}</div> : null}
 </div>
 </div>
 </div>
 );
}

export function AdminPageShell({
 title,
 subtitle,
 actions = null,
 guideKey = null,
 guideTone = 'slate',
 children,
 className = '',
 contentClassName = 'space-y-5',
}) {
 const guide = guideKey ? ADMIN_PAGE_GUIDES[guideKey] : null;

 return (
 <div className={`${contentClassName} ${className}`}>
 <AdminPageHeader title={title} subtitle={subtitle} actions={actions} />
 {guide ? (
 <AdminGuideCallout
 title={guide.title}
 steps={guide.steps}
 tone={guideTone}
 icon={ListChecks}
 />
 ) : null}
 {children}
 </div>
 );
}

export function AdminEmptyState({
 icon: Icon,
 title,
 description,
 actionLabel,
 actionTo,
 onAction,
 tips = [],
 className = '',
}) {
 return (
 <div
 className={`product-panel border-dashed p-8 text-center sm:p-10 ${className}`}
 >
 {Icon ? (
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-product-md border border-border bg-surface-muted text-ink-tertiary">
 <Icon className="h-7 w-7" aria-hidden />
 </div>
 ) : null}
 <p className="mb-1 font-semibold text-ink">{title}</p>
 {description ? (
 <p className="mx-auto mb-4 max-w-md text-sm text-ink-tertiary">{description}</p>
 ) : null}
 {tips.length > 0 ? (
 <ul className="mx-auto mb-5 max-w-sm space-y-1.5 text-left text-sm text-ink-secondary">
 {tips.map((tip, i) => (
 <li key={i} className="flex gap-2">
 <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ink" />
 <span>{tip}</span>
 </li>
 ))}
 </ul>
 ) : null}
 {actionLabel && (actionTo || onAction) ? (
 actionTo ? (
 <Link
 to={actionTo}
 className="inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-md bg-ink text-white hover:bg-black"
 >
 {actionLabel}
 <ArrowRight className="w-4 h-4" />
 </Link>
 ) : (
 <button
 type="button"
 onClick={onAction}
 className="inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-md bg-ink text-white hover:bg-black"
 >
 {actionLabel}
 <ArrowRight className="w-4 h-4" />
 </button>
 )
 ) : null}
 </div>
 );
}
